/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `goal` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class GoalMesg : Mesg {
    public constructor() : super("Goal", Profile.MesgNum.GOAL)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** sport. */
    public var sport: Sport?
        get() = (getFieldValue(SPORT_FIELD_NUM) as? UByte)?.let { Sport.fromValue(it) }
        set(value) = setFieldValue(SPORT_FIELD_NUM, value?.value)

    /** sub_sport. */
    public var subSport: SubSport?
        get() = (getFieldValue(SUB_SPORT_FIELD_NUM) as? UByte)?.let { SubSport.fromValue(it) }
        set(value) = setFieldValue(SUB_SPORT_FIELD_NUM, value?.value)

    /** start_date. */
    public var startDate: Instant?
        get() = (getFieldValue(START_DATE_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(START_DATE_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** end_date. */
    public var endDate: Instant?
        get() = (getFieldValue(END_DATE_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(END_DATE_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** type. */
    public var type: Goal?
        get() = (getFieldValue(TYPE_FIELD_NUM) as? UByte)?.let { Goal.fromValue(it) }
        set(value) = setFieldValue(TYPE_FIELD_NUM, value?.value)

    /** value. */
    public var value: UInt?
        get() = getFieldValue(VALUE_FIELD_NUM) as? UInt
        set(value) = setFieldValue(VALUE_FIELD_NUM, value)

    /** repeat. */
    public var repeat: Boolean?
        get() = (getFieldValue(REPEAT_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(REPEAT_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** target_value. */
    public var targetValue: UInt?
        get() = getFieldValue(TARGET_VALUE_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TARGET_VALUE_FIELD_NUM, value)

    /** recurrence. */
    public var recurrence: GoalRecurrence?
        get() = (getFieldValue(RECURRENCE_FIELD_NUM) as? UByte)?.let { GoalRecurrence.fromValue(it) }
        set(value) = setFieldValue(RECURRENCE_FIELD_NUM, value?.value)

    /** recurrence_value. */
    public var recurrenceValue: UShort?
        get() = getFieldValue(RECURRENCE_VALUE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(RECURRENCE_VALUE_FIELD_NUM, value)

    /** enabled. */
    public var enabled: Boolean?
        get() = (getFieldValue(ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** source. */
    public var source: GoalSource?
        get() = (getFieldValue(SOURCE_FIELD_NUM) as? UByte)?.let { GoalSource.fromValue(it) }
        set(value) = setFieldValue(SOURCE_FIELD_NUM, value?.value)

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val SPORT_FIELD_NUM: UByte = 0u
        public const val SUB_SPORT_FIELD_NUM: UByte = 1u
        public const val START_DATE_FIELD_NUM: UByte = 2u
        public const val END_DATE_FIELD_NUM: UByte = 3u
        public const val TYPE_FIELD_NUM: UByte = 4u
        public const val VALUE_FIELD_NUM: UByte = 5u
        public const val REPEAT_FIELD_NUM: UByte = 6u
        public const val TARGET_VALUE_FIELD_NUM: UByte = 7u
        public const val RECURRENCE_FIELD_NUM: UByte = 8u
        public const val RECURRENCE_VALUE_FIELD_NUM: UByte = 9u
        public const val ENABLED_FIELD_NUM: UByte = 10u
        public const val SOURCE_FIELD_NUM: UByte = 11u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[0] = Field("Sport", 0u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[1] = Field("SubSport", 1u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[2] = Field("StartDate", 2u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[3] = Field("EndDate", 3u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[4] = Field("Type", 4u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[5] = Field("Value", 5u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[6] = Field("Repeat", 6u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[7] = Field("TargetValue", 7u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[8] = Field("Recurrence", 8u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[9] = Field("RecurrenceValue", 9u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[10] = Field("Enabled", 10u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[11] = Field("Source", 11u, BaseType.ENUM, 1.0, 0.0, "", false)
            return fields
        }
    }
}
